/**
 * Donut chart "Gastos por categoria" con tooltip de subcategorias al
 * hover.
 *
 * Diseño compacto. La leyenda lateral muestra solo categorias padre
 * con monto y %; el desglose por subcategoria se revela como tooltip
 * flotante al hover sobre la fila del padre (no via Recharts tooltip,
 * sino via React state controlado para mantener estilo consistente
 * con el resto del dashboard).
 *
 * Opción visual elegida en Sprint 17 Fase 2 (preview /dev/tesoreria-home
 * mostro variante B vs C; ganó B por compactness).
 */
import { useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { GastoPorCategoria } from '../../lib/api/dashboard-tesoreria';
import { Monto, Num } from './Monto';

export interface DonutChartGastosProps {
  items: GastoPorCategoria[];
  total: number;
  size?: number;
}

export function DonutChartGastos({
  items,
  total,
  size = 200,
}: DonutChartGastosProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const data = items.map((it) => ({
    name: it.categoria_nombre,
    value: it.monto,
    color: it.categoria_color,
  }));

  if (items.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-text-muted"
        style={{ height: size }}
      >
        Sin gastos del mes registrados.
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 flex-wrap">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={size * 0.32}
              outerRadius={size * 0.48}
              startAngle={90}
              endAngle={-270}
              strokeWidth={2}
              stroke="#141416"
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color || '#8a8479'} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          aria-hidden="true"
        >
          <div className="text-[10px] uppercase tracking-wide text-text-muted">
            Total
          </div>
          <div className="text-base font-medium text-text-primary">
            <Monto value={total} />
          </div>
        </div>
      </div>

      <ul className="flex-1 min-w-[200px] space-y-1">
        {items.map((it) => {
          const pct = total > 0 ? Math.round((it.monto / total) * 100) : 0;
          const tieneHijos = it.hijos.length > 0;
          const isHovered = hovered === it.categoria_id;
          return (
            <li
              key={it.categoria_id}
              onMouseEnter={() =>
                tieneHijos && setHovered(it.categoria_id)
              }
              onMouseLeave={() => setHovered(null)}
              className={`relative rounded ${
                tieneHijos ? 'cursor-help' : ''
              }`}
            >
              <div
                className={`flex items-center gap-2 text-xs px-1 py-0.5 ${
                  tieneHijos ? 'hover:bg-white/[0.04]' : ''
                }`}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: it.categoria_color || '#8a8479',
                  }}
                />
                <span className="text-text-secondary flex-1 truncate">
                  {it.categoria_nombre}
                  {tieneHijos && (
                    <span className="text-text-muted ml-1">
                      ({it.hijos.length})
                    </span>
                  )}
                </span>
                <span className="text-text-primary">
                  <Monto value={it.monto} />
                </span>
                <Num className="text-text-muted w-9 text-right">{pct}%</Num>
              </div>
              {isHovered && tieneHijos && (
                <div className="absolute left-0 top-full mt-1 z-10 bg-bg-card border border-white/[0.1] rounded shadow-lg p-2 min-w-[200px] text-xs">
                  <div className="text-[10px] uppercase tracking-wide text-text-muted mb-1">
                    Desglose
                  </div>
                  <ul className="space-y-0.5">
                    {it.hijos.map((h) => {
                      const pctHijo =
                        it.monto > 0
                          ? Math.round((h.monto / it.monto) * 100)
                          : 0;
                      return (
                        <li
                          key={h.categoria_id}
                          className="flex items-center gap-2"
                        >
                          <span className="text-text-secondary flex-1 truncate">
                            {h.categoria_nombre}
                          </span>
                          <span className="text-text-primary">
                            <Monto value={h.monto} />
                          </span>
                          <Num className="text-text-muted w-9 text-right">
                            {pctHijo}%
                          </Num>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
